import { DEFAULT_CRON } from "@/lib/cron-defaults";

/**
 * Schedule-mode derivation and mode transitions, kept free of React so the zod
 * resolvers and the unit tests can use the exact logic the scheduler UI runs on.
 * `-scheduled-job-schedule.tsx` re-exports everything here for its callers.
 */

export const EVERY_UNIT_MS = { minutes: 60_000, hours: 3_600_000 } as const;
export type EveryUnit = keyof typeof EVERY_UNIT_MS;

export const ScheduleStartExisting = ["recurring", "once", "draft"] as const;
export const ScheduleStartAll = [...ScheduleStartExisting, "runNow"] as const;
export type ScheduleStartExisting = (typeof ScheduleStartExisting)[number];
// "interval" is opt-in only (via allowedScheduleTypes) - not part of the
// default option set so existing job types are unaffected.
export type ScheduleStart = (typeof ScheduleStartAll)[number] | "interval";

/** The scheduler fields the mode is derived from. Dates may arrive as strings from the API. */
export type ScheduleValues = {
  cronStart?: string | null;
  cronEnd?: string | null;
  every?: number | null;
  startDate?: Date | string | null;
  runNow?: boolean | null;
};

/**
 * Derives the schedule mode from raw form/data values. Exported for callers that
 * have no React context to read `useScheduleStart()` from, such as a zod resolver.
 *
 * Order matters: "interval" also sets `startDate`, so `every` has to be tested
 * before `startDate` or interval reads back as "once".
 */
export function scheduleStartOf(
  values: ScheduleValues | undefined | null,
): ScheduleStart {
  return values?.cronStart
    ? "recurring"
    : values?.every
      ? "interval"
      : values?.startDate
        ? "once"
        : values?.runNow
          ? "runNow"
          : "draft";
}

export type ScheduleTransitionPatch = {
  cronStart?: string | null;
  cronEnd?: string | null;
  every?: number | null;
  startDate?: Date | string | null;
  runNow?: boolean;
};

/** Five hours ahead: the default "once" start, far enough out to survive a slow form. */
const DEFAULT_ONCE_LEAD_MS = 18_000_000;
const DEFAULT_EVERY_MS = EVERY_UNIT_MS.minutes * 5;

/**
 * The form patch that moves the scheduler from its current values to `next`.
 *
 * Two invariants this has to hold, both of which broke in the past:
 *
 * 1. `scheduleStartOf({ ...current, ...patch }) === next`. The Window step and the
 *    zod resolver derive the mode from the raw values, while the scheduler UI holds
 *    it in context state; if the two disagree the user gets a validation message
 *    about fields that are not on the screen. Recurring therefore clears `startDate`
 *    (a lingering one from a previous "once" pick used to read back as "once").
 * 2. Recurring starts from a *submittable* cron. `CronInput` renders `DEFAULT_CRON`
 *    when the value is empty but deliberately does not emit onChange on mount, so
 *    seeding `""` made the visible default unsubmittable until the user touched a
 *    control. Seeding the same default the input shows closes that gap.
 *
 * `isNullable` mirrors the schedule-edit screen, where cleared fields must be sent
 * as `null` so the backend actually clears them; the creation wizard omits them.
 */
export function scheduleTransition(
  next: ScheduleStart,
  current: ScheduleValues | null | undefined,
  isNullable: boolean,
  now: Date = new Date(),
): ScheduleTransitionPatch {
  const clear = isNullable ? null : undefined;
  const notRunNow = isNullable ? false : undefined;
  switch (next) {
    case "recurring":
      return {
        every: clear,
        cronStart: current?.cronStart || DEFAULT_CRON,
        startDate: clear,
        runNow: notRunNow,
      };
    case "interval":
      return {
        cronStart: clear,
        cronEnd: clear,
        startDate: current?.startDate ?? now,
        every: current?.every ?? DEFAULT_EVERY_MS,
        runNow: notRunNow,
      };
    case "once":
      return {
        cronStart: clear,
        cronEnd: clear,
        every: clear,
        startDate: current?.startDate ?? new Date(+now + DEFAULT_ONCE_LEAD_MS),
        runNow: notRunNow,
      };
    case "runNow":
      return {
        cronStart: clear,
        cronEnd: clear,
        every: clear,
        startDate: clear,
        runNow: true,
      };
    case "draft":
      return {
        cronStart: clear,
        cronEnd: clear,
        every: clear,
        startDate: clear,
        runNow: notRunNow,
      };
  }
}
